import cx from 'clsx';
import { format, formatDistanceToNow } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as NoResultsDarkIcon } from '@assets/svg/ic-no-results-ai-dark.svg';
import { ReactComponent as NoResultsIcon } from '@assets/svg/ic-no-results-ai.svg';
import { ReactComponent as ThinArrowIcon } from '@assets/svg/thin-arrow.svg';

import StatusTypeItem from '@components/common/StatusTypeItem/StatusTypeItem';
import TooltipStatus from '@components/common/StatusTypeItem/TooltipStatus';
import TableEmptyRowExtended from '@components/common/TableEmptyRowExtended/TableEmptyRowExtended';

import useTranslation from '@hooks/useTranslation';

import type { IActionItemGlobalItemOrId } from '@model/actionItem';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';
import { selectAppPersistDarkMode, selectAppPersistOnBoardStart } from '@store/useAppStorePersist';

import { MOCKUP_AIS_LIST } from '../../constants/mockupAIsList';
import { getSortedData } from '../../helpers/getSortedData';
import {
  selectAISort,
  selectActionItemsRecentlyViewed,
  selectActionItemsViewed,
  useActionItemsStoreAction,
} from '../../store/useActionItemsStore';
import ActionItemTableMenu from '../ActionItemTableMenu/ActionItemTableMenu';
import style from './style.module.scss';

interface IProps {
  items: IActionItemGlobalItemOrId[];
  title: string;
  hasWorkOrder: boolean;
  isFilteringActive: boolean;
  tourId?: string;
  tooltipText?: string;
}

const ActionItemsTableBody: React.FC<IProps> = ({
  items,
  title,
  hasWorkOrder,
  isFilteringActive,
  tourId,
  tooltipText = '',
}) => {
  const { t } = useTranslation();
  const { data: profileData } = useUserProfileQuery();
  const [collapsedRows, setCollapsedRows] = useState(false);
  const { customerId, locationId } = useParams();

  const { setLastViewedAiId } = useActionItemsStoreAction();

  const locationSelectValue = selectAppLocationSelectValue();
  const viewedAIId = selectActionItemsViewed();
  const recentlyViewedAIId = selectActionItemsRecentlyViewed();
  const sort = selectAISort();
  const onBoardStart = selectAppPersistOnBoardStart();
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const darkMode = selectAppPersistDarkMode();

  const tableData: IActionItemGlobalItemOrId[] =
    !items.length && onBoardStart && tourId === 'tourStep-7' ? MOCKUP_AIS_LIST : getSortedData(items, sort); // Onboarding mockup only for Need Response block with tourId tourStep-7

  const handleHideRows = () => {
    setCollapsedRows(!collapsedRows);
  };

  const aiTypeTooltips: { [key: string]: string } = {
    regular: t('An action item indicating a potential issue with the monitored equipment.'),
    waitesWireless: t(
      'An action item for Waites monitoring hardware (i.e. sensors, nodes, repeaters or gateways).',
    ),
  };

  return (
    <div className={cx(style.tableBody, { [style.onboardingTable]: onBoardStart })}>
      <div className={style.tableBodyRowCategory} onClick={handleHideRows}>
        <span className={style.arrowWrapper}>
          <ThinArrowIcon className={cx({ [style.expandArrow]: collapsedRows })} />
        </span>
        <div
          data-tooltip-id="ai-table-tooltip"
          data-tooltip-content={tooltipText}
          data-tooltip-place="right"
          className={style.categoryName}
          id={tourId}
        >
          {title} - {tableData.length}
        </div>
      </div>
      {!collapsedRows && tableData.length === 0 && (
        <table className={style.tableBodyRowEmpty}>
          <tbody>
            <TableEmptyRowExtended
              icon={darkMode ? <NoResultsDarkIcon /> : <NoResultsIcon />}
              text={
                isFilteringActive
                  ? t('There are no action items that match your search/filter criteria.')
                  : t('Nothing to do right now.')
              }
            />
          </tbody>
        </table>
      )}
      {!collapsedRows &&
        tableData.map((item, index) => (
          <div
            key={item.id}
            id={item.id.toString()}
            className={cx(style.tableBodyRowWrapper, {
              [style.tableBodyRowBold]:
                viewedAIId[+item.id] !== undefined ? !viewedAIId[item.id] : !item.is_viewed,
              [style.tableBodyRowRecentlyViewed]: +recentlyViewedAIId === item.id,
            })}
          >
            <Link
              id={index === 0 ? 'tourStep-12' : undefined}
              to={`/customer/${customerId}/ai/${item.id}`}
              state={{ locationId: locationId, prevPage: `/customer/${customerId}` }}
              className={cx(style.tableBodyRow, {
                [style.urgencyStandard]: +item.urgency === 1,
                [style.urgencyHigh]: +item.urgency === 2,
                [style.urgencyCritical]: +item.urgency === 3,
              })}
              onClick={() => setLastViewedAiId(item.id)}
              data-qa="tableBodyRow"
            >
              <div
                className={cx(style.tableBodyRowLocation, {
                  [style.tableBodyRowLocationWithoutWo]: !hasWorkOrder,
                })}
              >
                <div className={style.tableBodyRowLocationName} data-qa="location">
                  {item.location_name}
                </div>
                {(locationSelectValue.id === 'all' || locationSelectValue.level_name === 'group') && (
                  <div className={style.tableRowAdditionalInfo}>{item.facility_name}</div>
                )}
              </div>
              <div
                className={cx(style.tableBodyRowEquipment, {
                  [style.tableBodyRowEquipmentWithoutWo]: !hasWorkOrder,
                })}
              >
                <div className={style.tableBodyRowEquipmentSmall} data-qa="equipment">
                  {item.equipment_name}
                </div>
                <div className={cx(style.tableRowAdditionalInfo, style.tableCellsHide)} data-qa="component">
                  {item.component_name}
                </div>
              </div>
              <div
                className={cx(style.tableBodyRowComponent, {
                  [style.tableBodyRowComponentWithoutWo]: !hasWorkOrder,
                })}
                data-qa="component"
              >
                {item.component_name}
              </div>
              <div
                className={cx(style.tableBodyRowType, {
                  [style.tableBodyRowTypeWithoutWo]: !hasWorkOrder,
                })}
              >
                <div data-qa="type">
                  <span data-tooltip-id="ai-table-tooltip" data-tooltip-content={aiTypeTooltips[item.type]}>
                    {item.type === 'regular' ? t('Regular') : t('Hardware')}
                  </span>
                </div>
                <div className={cx(style.tableRowAdditionalInfo, style.tableCellsHide)}>{item.wo_number}</div>
                <div className={cx(style.tableRowAdditionalInfo, style.tableCellTitle)}>
                  <span data-tooltip-id="ai-table-tooltip" data-tooltip-content={item.title}>
                    {item.title}
                  </span>
                </div>
              </div>
              {hasWorkOrder && <div className={style.tableBodyRowWo}>{item.wo_number}</div>}
              {profileData && (
                <div className={style.tableBodyRowTime}>
                  <div>{item.lifeTime}</div>
                  <div className={style.tableRowAdditionalInfo}>
                    {`${item.time_created} - ${item.updated_at}`}
                  </div>
                </div>
              )}
              <div className={style.tableBodyRowUpdate}>
                <div className={style.tableRowUpdatedInfo}>{item.update_distance}</div>
                <StatusTypeItem item={item} showCommentsCount={true} tooltipId="status-tooltip" />
              </div>
            </Link>
            <ActionItemTableMenu item={item} />
          </div>
        ))}
      <Tooltip className={style.aiTableTooltip} id="ai-table-tooltip" />
      <TooltipStatus id="status-tooltip" />
    </div>
  );
};

export default ActionItemsTableBody;
